import type { GenericObject, PaymentProfitDetails } from '@makemydeal/dr-platform-types';
import type { DeepPartial, Reducer } from 'redux';
import {
    ADD_DUE_AT_SIGNING_INFO_MESSAGE,
    APPLY_DUE_AT_SIGNING,
    APPLY_DUE_AT_SIGNING_FAILURE,
    APPLY_DUE_AT_SIGNING_SUCCESS,
    CLEAR_DUE_AT_SIGNING_ERRORS,
    CLEAR_DUE_AT_SIGNING_INFO_MESSAGES,
    DAS_CLEAR_PAYMENT,
    DAS_UPDATE_DOWNPAYMENT,
    DAS_UPDATE_EQUITY_ALLOCATION_METHOD,
    DAS_UPDATE_FEES_CAPITALIZED,
    DAS_UPDATE_FIRSTPAYMENT,
    DAS_UPDATE_TARGET_CASH_AMOUNT_PAID,
    DAS_UPDATE_TAXES_CAPITALIZED,
    DUE_AT_SIGNING_SLIDE_OUT_TOGGLE,
    REMOVE_DUE_AT_SIGNING_INFO_MESSAGE,
    REQUEST_DUE_AT_SIGNING,
    REQUEST_DUE_AT_SIGNING_FAILURE,
    REQUEST_DUE_AT_SIGNING_SUCCESS
} from '../actionTypes/dueAtSigningActionTypes';

import { parseErrorMessages } from '../utils/psErrorUtils';

import type { DasOverrides, DasPayment, DasState, DasToggleOpenPayload } from '@makemydeal/dr-dash-types';
import { initActionTypes } from '@makemydeal/dr-shared-store';
import { isDASRequestAborted } from '../utils/dueAtSigningUtils';

export type RequestRollToOptionsPayload = { balanceDue?: number; payment?: number };

export type UpdateRollToOptionsPayload = { balanceDue?: number };

export type RequestRollToPaymentsFailurePayload = {
    response?: {
        errors?: GenericObject[];
        message?: string;
    };
};

export type RequestPaymentUPQPayload = {
    profit: PaymentProfitDetails | undefined;
    actionValue: number | undefined;
    totalTaxes: number | 0;
    balanceDue: number | 0;
    VppAndProtectionTotal: number | 0;
};

export type DasRequestMeta = { isDasPaymentRefreshEnabled?: boolean };

export type DasAction =
    | { type: typeof DAS_UPDATE_DOWNPAYMENT; payload: DasOverrides }
    | { type: typeof DAS_UPDATE_FIRSTPAYMENT; payload: DasOverrides }
    | { type: typeof DAS_UPDATE_FEES_CAPITALIZED; payload: DasOverrides }
    | { type: typeof DAS_UPDATE_TAXES_CAPITALIZED; payload: DasOverrides }
    | { type: typeof DAS_UPDATE_TARGET_CASH_AMOUNT_PAID; payload: DasOverrides }
    | { type: typeof ADD_DUE_AT_SIGNING_INFO_MESSAGE; payload: { heading: string; content: string } }
    | { type: typeof REMOVE_DUE_AT_SIGNING_INFO_MESSAGE; payload: number }
    | { type: typeof CLEAR_DUE_AT_SIGNING_INFO_MESSAGES }
    | { type: typeof DUE_AT_SIGNING_SLIDE_OUT_TOGGLE; payload?: DasToggleOpenPayload }
    | { type: typeof APPLY_DUE_AT_SIGNING_SUCCESS }
    | { type: typeof APPLY_DUE_AT_SIGNING_FAILURE; payload: any }
    | { type: typeof APPLY_DUE_AT_SIGNING }
    | { type: typeof DAS_UPDATE_EQUITY_ALLOCATION_METHOD; payload: DasOverrides }
    | { type: typeof REQUEST_DUE_AT_SIGNING; payload: RequestPaymentUPQPayload }
    | { type: typeof REQUEST_DUE_AT_SIGNING_FAILURE; payload: any; meta?: DasRequestMeta }
    | { type: typeof CLEAR_DUE_AT_SIGNING_ERRORS }
    | { type: typeof DAS_CLEAR_PAYMENT }
    | {
          type: typeof REQUEST_DUE_AT_SIGNING_SUCCESS;
          payload?: DasPayment;
          meta?: DasRequestMeta;
      }
    | {
          type: typeof initActionTypes.INIT_SUCCESS;
          payload: DeepPartial<{
              calculationDetails: {
                  payment: {
                      firstPayment: {
                          amount: number;
                          paymentDate: string;
                      };
                      isFirstPaymentCapitalized: boolean;
                      isCapReductionTaxCapitalized: boolean;
                  };
              };
              offer: {
                  downPayment: number;
              };
          }>;
      };

export type DueAtSigningReducer = Reducer<DasState, DasAction>;

export const INITIAL_DAS_STATE: DasState = {
    errors: [],
    messages: [],
    isSlideOutOpen: false,
    payment: {},
    overrides: {}
};

export const initialState: DasState = structuredClone(INITIAL_DAS_STATE);

// A capitalized first payment is rolled into cap cost, so the slideout renders it as $0.
const zeroFirstPaymentIfCapitalized = (payment: DasPayment, isFirstPaymentCapitalized?: boolean): DasPayment =>
    isFirstPaymentCapitalized ? { ...payment, firstPayment: 0 } : payment;

export const reducer: DueAtSigningReducer = (state = initialState, action: DasAction) => {
    switch (action.type) {
        case initActionTypes.INIT_SUCCESS: {
            const calculationDetailsPayment = action.payload?.calculationDetails?.payment;

            if (!calculationDetailsPayment) {
                return state;
            }

            // getDasDefaultOverrides on slide-out open. Keep overrides empty because getDasDefaultOverrides only fills null fields
            return {
                ...state,
                overrides: {}
            };
        }

        case REQUEST_DUE_AT_SIGNING: {
            return {
                ...state,
                isCalculating: true,
                errors: []
            };
        }

        case REQUEST_DUE_AT_SIGNING_SUCCESS: {
            if (!action.payload && action.meta?.isDasPaymentRefreshEnabled) {
                return {
                    ...state,
                    isCalculating: false,
                    errors: [{ message: 'An unexpected error occurred' }]
                };
            }

            const { firstPaymentCapitalized, targetCashAmountPaid, ...payment } = action.payload as DasPayment;
            const errors =
                parseErrorMessages({
                    ...payment,
                    rollProgramItems: [],
                    messages: []
                }) || [];
            return {
                ...state,
                errors,
                payment: zeroFirstPaymentIfCapitalized(payment, firstPaymentCapitalized),
                overrides: {
                    ...state.overrides,
                    firstPaymentCapitalized: firstPaymentCapitalized ?? false,
                    targetCashAmountPaid: targetCashAmountPaid ?? false
                },
                isCalculating: false
            };
        }

        case REQUEST_DUE_AT_SIGNING_FAILURE: {
            const { response, message } = action.payload;

            if (isDASRequestAborted(message)) {
                if (action.meta?.isDasPaymentRefreshEnabled) {
                    return {
                        ...state,
                        errors: []
                    };
                }
                return {
                    ...state,
                    isCalculating: false,
                    errors: []
                };
            }

            const errors = response?.errors || [];

            const errorMessage = !errors.length ? response?.message || 'An unexpected error occurred' : '';

            if (errorMessage) {
                errors.push({ message: errorMessage });
            }

            return {
                ...state,
                isCalculating: false,
                errors: [...(state.errors || []), ...errors] // !IMPORTANT Combine errors from UPQ and UPR
            };
        }
        case DAS_UPDATE_DOWNPAYMENT:
        case DAS_UPDATE_FIRSTPAYMENT:
        case DAS_UPDATE_FEES_CAPITALIZED:
        case DAS_UPDATE_TAXES_CAPITALIZED:
        case DAS_UPDATE_EQUITY_ALLOCATION_METHOD:
        case DAS_UPDATE_TARGET_CASH_AMOUNT_PAID: {
            const overrides = action.payload;
            return {
                ...state,
                isCalculating: false,
                errors: [],
                overrides: {
                    ...state.overrides,
                    ...overrides
                }
            };
        }
        case ADD_DUE_AT_SIGNING_INFO_MESSAGE: {
            const currentMessages = state.messages || [];
            const { heading, content } = action.payload;

            if (!currentMessages.some((m) => m.content === content)) {
                return {
                    ...state,
                    messages: [...currentMessages, { heading, content }]
                };
            }
            return state;
        }
        case REMOVE_DUE_AT_SIGNING_INFO_MESSAGE: {
            const messages = state.messages || [];
            return {
                ...state,
                messages: messages.filter((_, index) => index !== action.payload)
            };
        }
        case CLEAR_DUE_AT_SIGNING_INFO_MESSAGES: {
            return {
                ...state,
                messages: []
            };
        }
        case DUE_AT_SIGNING_SLIDE_OUT_TOGGLE: {
            if (state.isSlideOutOpen) {
                // close: reset fully so the next open re-derives payment/overrides live from offer-redux
                return {
                    ...INITIAL_DAS_STATE,
                    isSlideOutOpen: false
                };
            }
            // open: no PO call fires here, so apply the same $0 rule to the seed
            if (!action.payload) {
                return { ...state, isApplied: false, isSlideOutOpen: true };
            }
            const { overrides, payment } = action.payload;
            return {
                ...state,
                overrides,
                payment: zeroFirstPaymentIfCapitalized(payment, overrides.firstPaymentCapitalized),
                isApplied: false,
                isSlideOutOpen: true
            };
        }

        case APPLY_DUE_AT_SIGNING: {
            return {
                ...state,
                isApplying: true,
                errors: []
            };
        }

        case APPLY_DUE_AT_SIGNING_SUCCESS: {
            // Applied values now live in offer-redux; reset DAS state so the next open re-derives them live
            // instead of restoring a stale snapshot.
            return {
                ...INITIAL_DAS_STATE,
                isApplied: true,
                isApplying: false
            };
        }

        case APPLY_DUE_AT_SIGNING_FAILURE: {
            return {
                ...state,
                isApplying: false
            };
        }
        case CLEAR_DUE_AT_SIGNING_ERRORS: {
            return {
                ...state,
                errors: []
            };
        }
        case DAS_CLEAR_PAYMENT: {
            return {
                ...state,
                payment: {},
                isCalculating: true
            };
        }

        default: {
            return state;
        }
    }
};
